// src/store/course/actions.js

import {
  SET_COURSE_DATA,
  SET_COURSE_LOADING,
  SET_COURSE_ERROR,
  SET_CURRENT_LESSON_PROGRESS,
  SET_CURRICULUM_GRADE_AND_SUBJECT,
  SET_CURRICULUM_LIST,
  SET_SHOW_UNIT_SELECTION_SCREEN,
  SET_LAST_QUESTION,
  SET_START__LAST_QUESTION_ONE_TO_ONE,
} from './actionTypes';
import {BASE_URL, ALL_CURRICULAM_DETAILS_API, API_KEY} from '@env';

import {setIsQuikbase} from '../meeting/actions/meeting';
import course from '../communication/reducers/course';

import curriculumByIdData from '../../curriculumByIdData';
import curriculumListData from '../../curriculumListData';

export const setCourseData = courseData => ({
  type: SET_COURSE_DATA,
  payload: courseData,
});

export const setCourseLoading = loading => ({
  type: SET_COURSE_LOADING,
  payload: loading,
});

export const setCourseError = error => ({
  type: SET_COURSE_ERROR,
  payload: error,
});

export const setCurrentLessonProgress = currentLessonProgress => ({
  type: SET_CURRENT_LESSON_PROGRESS,
  payload: currentLessonProgress,
});

export const setCurriculumGradeAndSubject = curriculumGradeAndSubject => ({
  type: SET_CURRICULUM_GRADE_AND_SUBJECT,
  payload: curriculumGradeAndSubject,
});

export const setCurriculumList = curriculumList => ({
  type: SET_CURRICULUM_LIST,
  payload: curriculumList,
});

export const setShowUnitSelectionScreen = show => ({
  type: SET_SHOW_UNIT_SELECTION_SCREEN,
  payload: show,
});

export const setLastQuestion = lastQuestion => ({
  type: SET_LAST_QUESTION,
  payload: lastQuestion,
});

export const setStartLastQuestionOneToOne = start => ({
  type: SET_START__LAST_QUESTION_ONE_TO_ONE,
  payload: start,
});

export const fetchCourseData =
  ({
    currentCurriculumId,
    currentUnitId,
    token,
    worldToLoad,
    curriculumList,
    isTokenBasedFetching,
    grade,
    subject,
  }) =>
  async dispatch => {
    dispatch(setCourseLoading(true));

    let isQuikbase = worldToLoad === 'quikbase';

    let course;

    try {
      const accessToken = token;
      let endpoint;
      let response;
      let json;

      if (curriculumList === null) {
        endpoint = process.env.CURRICULUM_LIST_API;
        response = await fetch(`${BASE_URL}${endpoint}`, {
          headers: {
            'x-api-key': API_KEY,
          },
        });

        if (!response.ok) {
          throw new Error('Server error');
        }
        json = await response.json();
        if (json && json.data) {
          curriculumList = json.data;
          dispatch(setCurriculumList(curriculumList));
        } else {
          throw new Error('No curriculum list data');
        }
      }

      let curriculumId;
      
      course = curriculumByIdData[0];

      dispatch(
        setCurriculumGradeAndSubject({
          grade: course.grade,
          subject: course.subject,
        }),
      );

      // Process the course data (same mapping logic as before)
      const courseTitle = course.longCourseName || 'Course Title Placeholder';
      let mappedUnits = course.units.map((unit, index) => ({
        id: unit.unitId,
        unitNumber: index + 1,
        title: unit.qlUnit,
        description: unit.qlUnitDescription,
        unitUrl: unit.unitUrl,
        lessons: unit.lessons,
        unitImage: unit.unitImage,
        lessonMasteryScore: unit.lessonMasteryScore,
      }));

      const lessonMasteryThreshold = 90;

      let currentUnitIndex;
      let currentUnit;
      if (!isQuikbase) {
        currentUnitIndex = mappedUnits.findIndex(
          unit => unit.id === currentUnitId,
        );

        currentUnit = mappedUnits.find(unit => unit.id === currentUnitId);
        if (!currentUnit && mappedUnits.length > 0) {
          currentUnitIndex = 0;
          currentUnit = mappedUnits[0];
        }
      }

      const processedUnits = mappedUnits.map((unit, index) => {
        const filteredUnit = course.units.find(u => u.unitId === unit.id);
        if (filteredUnit) {
          const processedLessons = unit.lessons.map(lesson => {
            const filteredLesson = filteredUnit.lessons.find(
              l => l.lessonId === lesson.lessonId,
            );

            const lessonMasteryPercentage = filteredLesson
              ? Math.round(
                  (filteredLesson.masteryScore /
                    filteredUnit.lessonMasteryScore) *
                    100,
                )
              : 0;

            const progress = filteredLesson ? filteredLesson.progress : 0;
            const currentSlide = Math.round(
              (progress * lesson.totalSlides) / 100,
            );
            return {
              ...lesson,
              currentSlide: currentSlide,
              isMastered: lessonMasteryPercentage >= lessonMasteryThreshold,
            };
          });
          const totalLessons = processedLessons.length;
          const lessonsMastered = processedLessons.filter(
            l => l.isMastered,
          ).length;
          const unitMasteryPercentage =
            totalLessons > 0
              ? Math.round((lessonsMastered / totalLessons) * 100)
              : 0;
          const lessonsCompletedPercentage =
            totalLessons > 0
              ? Math.round(
                  processedLessons.reduce((acc, l) => acc + l.progress, 0) /
                    totalLessons,
                )
              : 0;
          const hundredPercentCompletedCount = processedLessons.filter(
            l => l.progress === 100,
          ).length;

          const isCurrentlyStudying = isQuikbase
            ? false
            : index === currentUnitIndex;

          return {
            ...unit,
            lessons: processedLessons,
            totalLessons,
            lessonsMastered,
            unitMasteryPercentage,
            lessonsCompletedPercentage,
            hundredPercentCompletedCount,
            isCurrentlyStudying,
          };
        }
        return unit;
      });

      const totalUnits = processedUnits.length;
      const unitsMastered = processedUnits.filter(
        u => u.unitMasteryPercentage >= lessonMasteryThreshold,
      ).length;
      const calculatedCourseMastery =
        totalUnits > 0 ? Math.round((unitsMastered / totalUnits) * 100) : 0;

      let nextUnitId = null;
      if (
        !isQuikbase &&
        typeof currentUnitIndex === 'number' &&
        currentUnitIndex < processedUnits.length - 1
      ) {
        nextUnitId = processedUnits[currentUnitIndex + 1].id;
      }

      const courseData = {
        courseTitle,
        curriculumId: course.id,
        units: processedUnits,
        courseMastery: calculatedCourseMastery,
        currentUnitIndex: isQuikbase ? -1 : currentUnitIndex,
        currentUnitUrl: isQuikbase ? '' : (currentUnit ? currentUnit.unitUrl : ''),
        nextUnitId: nextUnitId,
      };

      dispatch(setCourseData(courseData));
    } catch (error) {
      console.error(error);
      dispatch(setCourseError(error.toString()));
    } finally {
      dispatch(setIsQuikbase(isQuikbase));
      dispatch(setCourseLoading(false));
    }
  };
